import { useMemo } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { TradingToolItem } from "@/types/tradingTool";
import { cn } from "@/utils/utils";

const TOOL_ICONS = {
  symbol: "/dashboard/tools/symbol.svg",
  trading: "/dashboard/tools/trading.svg",
  chart: "/dashboard/tools/chart.svg",
  globe: "/dashboard/tools/globe.svg",
  square: "/dashboard/tools/square.svg",
  solid: "/dashboard/tools/solid.svg",
  crosshair: "/dashboard/tools/crosshair.svg",
  globesolid: "/dashboard/tools/globesolid.svg",
} as const;

type TradingToolDefinition = Omit<TradingToolItem, "title" | "description" | "cta"> & {
  titleKey: string;
  descriptionKey: string;
  ctaKey: string;
};

const TRADING_TOOL_DEFINITIONS: TradingToolDefinition[] = [
  {
    id: "symbols-hours",
    titleKey: "Symbols & Trading Hours",
    descriptionKey:
      "Get detailed insights into instrument availability and their respective market hours.",
    ctaKey: "Search Instruments",
    iconSrc: TOOL_ICONS.symbol,
    internalPath: "/user/trading-tool/symbols-hours",
    featured: true,
  },
  {
    id: "trading-view",
    titleKey: "Trading View",
    descriptionKey:
      "Access powerful charting tools, technical indicators, and real-time market data all in one place.",
    ctaKey: "Get Insights",
    iconSrc: TOOL_ICONS.trading,
    internalPath: "/user/trading-tool/trading-view",
  },
  {
    id: "traders-analysis",
    titleKey: "Trader's Analysis",
    descriptionKey:
      "Review your trading behavior, patterns, and performance to refine your strategy over time.",
    ctaKey: "Check your Performance",
    iconSrc: TOOL_ICONS.chart,
    internalPath: "/user/analyticsview",
  },
  {
    id: "timezone-converter",
    titleKey: "Time zone Converter",
    descriptionKey:
      "Easily convert global trading session times to your local timezone to stay aligned with the markets.",
    ctaKey: "Time your trades",
    iconSrc: TOOL_ICONS.globe,
    internalPath: "/user/trading-tool/timezone-converter",
  },
  {
    id: "lot-size-calculator",
    titleKey: "Lot Size Calculator",
    descriptionKey:
      "Quickly calculate the ideal lot size based on risk percentage, account balance, and stop-loss distance.",
    ctaKey: "Calculate lot size",
    iconSrc: TOOL_ICONS.square,
    internalPath: "/user/trading-tool/lot-size-calculator",
  },
  {
    id: "equity-simulator",
    titleKey: "Equity Simulator",
    descriptionKey:
      "Simulate your account growth over time based on win rate, risk, reward, and number of trades.",
    ctaKey: "Graph your progress",
    iconSrc: TOOL_ICONS.solid,
    internalPath: "/user/trading-tool/equity-simulator",
  },
  {
    id: "risk-manager-pro",
    titleKey: "Risk Manager Pro",
    descriptionKey:
      "Plan your trade risk with precision and maintain consistent risk-reward ratios.",
    ctaKey: "Unlock",
    iconSrc: TOOL_ICONS.crosshair,
    locked: true,
    lockPrice: "$49/mo",
  },
  {
    id: "trade-copier-elite",
    titleKey: "Trade Copier Elite",
    descriptionKey:
      "Mirror trades in real-time across multiple accounts with precision, speed, and zero manual effort.",
    ctaKey: "Unlock",
    iconSrc: TOOL_ICONS.globesolid,
    locked: true,
    lockPrice: "$99",
  },
];

function LockedBadge() {
  const { t } = useTranslation();

  return (
    <span className="absolute right-4 top-4 rounded-md bg-gradient-to-r from-[#1CCDE6] via-[#9ED473] to-[#DBD633] px-2.5 py-1 font-nexa text-xs font-bold uppercase tracking-wide text-black">
      {t("Locked")}
    </span>
  );
}

function ToolIconBox({
  iconSrc,
  title,
  isLocked = false,
}: {
  iconSrc: string;
  title: string;
  isLocked?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-md border",
        isLocked ? "border-white/5 bg-[#121212]" : "border-white/10 bg-white/10",
      )}
    >
      <img
        src={iconSrc}
        alt={title}
        className={cn(
          "h-[18px] w-[18px] object-contain",
          isLocked && "opacity-50 grayscale",
        )}
      />
    </div>
  );
}

function ToolCard({
  tool,
  onAction,
}: {
  tool: TradingToolItem;
  onAction: (tool: TradingToolItem) => void;
}) {
  const { t } = useTranslation();
  const isLocked = tool.locked;

  return (
    <article
      className={cn(
        "relative !flex flex-col rounded-2xl p-5",
        isLocked
          ? "bg-gradient-to-r from-[#1CCDE6] via-[#9ED473] to-[#DBD633] w-full  !items-start !justify-start p-[2px]"
          : "card-gradient-hover duration-300 transition-all border hover:!border-teal/40 bg-white/5 ",
        tool.featured && !isLocked && "overflow-hidden",
      )}
    >
      <div className={`${isLocked ? "p-5 bg-[#0b0b0b]  w-full h-full rounded-2xl" : ""}`}>
        {isLocked && <LockedBadge />}

        <div className="relative z-10 flex flex-1 flex-col w-full">
          <ToolIconBox iconSrc={tool.iconSrc} title={tool.title} isLocked={isLocked} />

          <h3
            className={cn(
              "mt-4 font-neue text-lg font-medium leading-snug",
              isLocked ? "text-white/30" : "text-white",
            )}
          >
            {tool.title}
          </h3>
          <p className={`mt-2 flex-1 font-nexa text-xs ${isLocked ? "text-white/20" : "text-white/45"}`}>
            {tool.description}
          </p>

          <button
            type="button"
            onClick={() => onAction(tool)}
            className={cn(
              "mt-5 flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 font-neue text-sm transition-colors",
              isLocked
                ? "btn-gradient-left text-white !p-[2px]"
                : "border border-white/10 bg-white/5 text-white/90 hover:border-white/12 hover:bg-white/10",
            )}
          >
            {isLocked ? (
              <>
                <Lock className="h-4 w-4 shrink-0 text-white" />
                <span>
                  {t("Unlock —")}{" "}
                  <span className="font-medium text-white">{tool.lockPrice}</span>
                </span>
              </>
            ) : (
              <>
                <span>{tool.cta}</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-white/60" />
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function TradingTool() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tradingTools = useMemo<TradingToolItem[]>(
    () =>
      TRADING_TOOL_DEFINITIONS.map((tool) => ({
        ...tool,
        title: t(tool.titleKey),
        description: t(tool.descriptionKey),
        cta: t(tool.ctaKey),
      })),
    [t],
  );

  const handleToolAction = (tool: TradingToolItem) => {
    if (tool.locked) {
      toast.info(t("{{title}} — subscription unlock coming soon", { title: tool.title }));
      return;
    }

    if (tool.internalPath) {
      navigate(tool.internalPath);
      return;
    }

    if (tool.href) {
      window.open(tool.href, "_blank", "noopener,noreferrer");
      return;
    }

    toast.info(t("This tool will be available soon"));
  };

  const unlockedTools = tradingTools.filter((tool) => !tool.locked);
  const lockedTools = tradingTools.filter((tool) => tool.locked);

  return (
    <div className="min-h-full w-full overflow-y-auto hide-scrollbar bg-[#050505] px-4 py-6 text-white sm:px-6 lg:px-8">
      <div className="">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 xl:gap-5">
          {unlockedTools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} onAction={handleToolAction} />
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 xl:gap-5">
          {lockedTools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} onAction={handleToolAction} />
          ))}
        </div>
      </div>
    </div>
  );
}
